import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Chip, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import { Title, useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { aiGeneratorService, apiErrorMessage, isJobRunning, type ContentJob, type CourseJob, type QuotaUsage } from '../../services/aiGeneratorService';
import { ContentJobForm, ContentJobResults } from './ContentGeneratorPanel';
import { CourseJobForm } from './CourseGeneratorPanel';
import './ai-generator.css';

type Mode = 'content' | 'course';
const POLL_MS = 4000;

const StatusChip: React.FC<{ status: ContentJob['status'] }> = ({ status }) => {
    const translate = useTranslate();
    return <Chip size="small" className={`oa-aig-status-chip is-${status}`} label={translate(`aiGenerator.status.${status}`)} />;
};

const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

/**
 * KI-Generator page (Tranche B2, #489): lesson building blocks from source
 * material, or a complete draft course from topic + goal. Jobs run in the
 * Messenger worker; the lists poll while a job is pending/processing.
 */
export const AiGeneratorPage: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const navigate = useNavigate();
    const [mode, setMode] = useState<Mode>('content');
    const [contentJobs, setContentJobs] = useState<ContentJob[]>([]);
    const [courseJobs, setCourseJobs] = useState<CourseJob[]>([]);
    const [quota, setQuota] = useState<QuotaUsage | null>(null);
    const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
    const [selectedJob, setSelectedJob] = useState<ContentJob | null>(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            if (mode === 'content') {
                const { jobs, quota: q } = await aiGeneratorService.listContentJobs();
                setContentJobs(jobs);
                setQuota(q);
            } else {
                const { jobs, quota: q } = await aiGeneratorService.listCourseJobs();
                setCourseJobs(jobs);
                setQuota(q);
            }
        } catch (e) {
            notify(apiErrorMessage(e, translate('aiGenerator.jobs.loadError')), { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [mode, notify, translate]);

    const loadSelected = useCallback(async () => {
        if (selectedJobId === null) { setSelectedJob(null); return; }
        try {
            setSelectedJob(await aiGeneratorService.getContentJob(selectedJobId));
        } catch (e) {
            notify(apiErrorMessage(e, translate('aiGenerator.jobs.loadError')), { type: 'error' });
        }
    }, [notify, selectedJobId, translate]);

    useEffect(() => { setLoading(true); void refresh(); }, [refresh]);
    useEffect(() => { void loadSelected(); }, [loadSelected]);

    const anyRunning = useMemo(
        () => (mode === 'content' ? contentJobs : courseJobs).some((j) => isJobRunning(j.status)) || (selectedJob !== null && isJobRunning(selectedJob.status)),
        [mode, contentJobs, courseJobs, selectedJob],
    );

    useEffect(() => {
        if (!anyRunning) return undefined;
        const timer = window.setInterval(() => { void refresh(); void loadSelected(); }, POLL_MS);
        return () => window.clearInterval(timer);
    }, [anyRunning, refresh, loadSelected]);

    const quotaLabel = quota
        ? mode === 'content'
            ? translate('aiGenerator.quota.content', { used: quota.content.used, limit: quota.content.limit })
            : translate('aiGenerator.quota.course', { used: quota.course.used, limit: quota.course.limit })
        : null;
    const quotaExhausted = quota ? (mode === 'content' ? quota.content.used >= quota.content.limit : quota.course.used >= quota.course.limit) : false;

    return (
        <div className="oa-ai-generator">
            <Title title={translate('aiGenerator.pageTitle')} />
            <div className="oa-aig-header">
                <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>{translate('aiGenerator.pageTitle')}</Typography>
                <Chip size="small" label={translate('aiGenerator.badge')} />
                {quotaLabel && <span className="oa-aig-quota" data-testid="quota">{quotaLabel}</span>}
            </div>
            <Tabs value={mode} onChange={(_, v: Mode) => { setMode(v); setSelectedJobId(null); }} aria-label={translate('aiGenerator.modes.label')}>
                <Tab value="content" label={translate('aiGenerator.modes.content')} />
                <Tab value="course" label={translate('aiGenerator.modes.course')} />
            </Tabs>
            {quotaExhausted && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{translate('aiGenerator.quota.exhausted')}</Typography>}

            <div className="oa-aig-layout">
                <div>
                    {mode === 'content' ? (
                        <>
                            <ContentJobForm disabled={quotaExhausted} onCreated={(job) => { setContentJobs((prev) => [job, ...prev]); setSelectedJobId(job.id); void refresh(); }} />
                            {selectedJob && <ContentJobResults job={selectedJob} onChanged={() => { void loadSelected(); void refresh(); }} />}
                        </>
                    ) : (
                        <CourseJobForm disabled={quotaExhausted} onCreated={(job) => { setCourseJobs((prev) => [job, ...prev]); void refresh(); }} />
                    )}
                </div>

                <aside className="oa-aig-card" aria-label={translate('aiGenerator.jobs.title')}>
                    <h2 className="oa-aig-card-title">
                        <span className="oa-aig-icon"><ListAltOutlined fontSize="small" /></span>
                        {translate('aiGenerator.jobs.title')}
                        <Button size="small" sx={{ ml: 'auto' }} startIcon={<RefreshOutlined />} onClick={() => { void refresh(); void loadSelected(); }}>{translate('aiGenerator.jobs.refresh')}</Button>
                    </h2>
                    {loading && <LinearProgress sx={{ mb: 1 }} />}
                    {!loading && (mode === 'content' ? contentJobs : courseJobs).length === 0 && <div className="oa-aig-empty">{translate('aiGenerator.jobs.empty')}</div>}
                    <div className="oa-aig-jobs">
                        {mode === 'content' && contentJobs.map((job) => (
                            <div key={job.id} className={`oa-aig-job${selectedJobId === job.id ? ' is-active' : ''}`}>
                                <div className="oa-aig-job-head">
                                    <button type="button" className="oa-aig-job-title" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit' }} onClick={() => setSelectedJobId(job.id)}>
                                        {translate('aiGenerator.jobs.contentJob', { id: job.id })} · {job.target_types.map((t) => translate(`aiGenerator.types.${t}`)).join(', ')}
                                    </button>
                                    <StatusChip status={job.status} />
                                </div>
                                <div className="oa-aig-job-meta">
                                    <span>{formatDate(job.created_at)}</span>
                                    <span>{translate(`aiGenerator.sources.${job.source_type}`)}</span>
                                    {job.status === 'completed' && <span>{translate('aiGenerator.jobs.items', { smart_count: job.content_count })}</span>}
                                </div>
                                {job.source_excerpt && <Typography variant="caption" color="text.secondary" component="div" className="oa-aig-job-excerpt">{job.source_excerpt}</Typography>}
                                {isJobRunning(job.status) && <div className="oa-aig-job-progress"><LinearProgress /></div>}
                                {job.status === 'failed' && job.error_message && <Typography variant="caption" color="error">{job.error_message}</Typography>}
                            </div>
                        ))}
                        {mode === 'course' && courseJobs.map((job) => (
                            <div key={job.id} className="oa-aig-job">
                                <div className="oa-aig-job-head">
                                    <span className="oa-aig-job-title">{job.course_title ?? job.topic}</span>
                                    <StatusChip status={job.status} />
                                </div>
                                <div className="oa-aig-job-meta">
                                    <span>{formatDate(job.created_at)}</span>
                                    <span>{job.language.toUpperCase()}</span>
                                    {job.difficulty && <span>{translate(`aiGenerator.difficulty.${job.difficulty}`)}</span>}
                                </div>
                                {job.goal && <Typography variant="caption" color="text.secondary" component="div" className="oa-aig-job-excerpt">{job.goal}</Typography>}
                                {isJobRunning(job.status) && (
                                    <div className="oa-aig-job-progress">
                                        <LinearProgress variant={job.total_lessons > 0 ? 'determinate' : 'indeterminate'} value={job.total_lessons > 0 ? Math.round((job.completed_lessons / job.total_lessons) * 100) : 0} />
                                        <span>{job.progress_label || translate(`aiGenerator.status.${job.status}`)}</span>
                                    </div>
                                )}
                                {job.status === 'failed' && job.error_message && <Typography variant="caption" color="error">{job.error_message}</Typography>}
                                {job.status === 'completed' && job.course_id && (
                                    <div>
                                        <Button size="small" startIcon={<OpenInNewOutlined />} onClick={() => navigate(`/courses/${job.course_id}/builder`)}>{translate('aiGenerator.jobs.openBuilder')}</Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default AiGeneratorPage;
